/**
 * useTrackModuleUsage — fires one `page_view` per module per browser
 * session (PLAN.md §4.3b, Faza E.4). `sessionStorage` guard so navigating
 * back and forth within the same SPA session doesn't spam the endpoint.
 */
import { useEffect } from 'react';

import { http } from '@/shared/lib/api';
import { useAuth } from '@/shared/auth/AuthContext';
import type { CollaborationModuleKey } from '@/shared/components/CollaborationBanner';

function sessionKey(moduleKey: CollaborationModuleKey, tenantId: string | null | undefined): string {
  return `vanagreen_module_usage_pv_${moduleKey}_${tenantId ?? 'none'}`;
}

/** Pass `null` when the module isn't active for the current tenant yet
 * (e.g. `partner_portal` behind its own entitlement check) — the hook
 * still runs (hooks can't be called conditionally) but records nothing. */
export function useTrackModuleUsage(moduleKey: CollaborationModuleKey | null): void {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  useEffect(() => {
    if (!user || !moduleKey) return;
    const key = sessionKey(moduleKey, tenantId);
    try {
      if (sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
    } catch {
      // sessionStorage unavailable — fire once per mount anyway, best-effort
    }
    void http.post('/api/module-usage', { module_key: moduleKey, action: 'page_view' }).catch(() => {
      // best-effort tracking — never surface an error to the user
    });
  }, [moduleKey, tenantId, user]);
}
